/**
 * Customization Settings Page Component
 * Manages theme colors for navigation and header
 * @component CustomizationSettings
 */

import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { translate } from '../../../locales';
import './CustomizationSettings.css';

const CustomizationSettings: React.FC = () => {
  const { language } = useLanguage();
  const { colors, updateTheme, resetNavigationTheme, resetHeaderTheme, isLoading: isUpdating, error: themeError } = useTheme();
  const { addNotification } = useNotification();
  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const [localColors, setLocalColors] = useState(colors);

  const handleColorChange = (key: keyof typeof colors, value: string) => {
    setLocalColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveTheme = async () => {
    try {
      await updateTheme(localColors);
      addNotification(t('notifications.success'), 'success');
      console.log('🎨 Тему успішно збережено');
    } catch (error) {
      console.error('❌ Помилка збереження теми:', error);
      addNotification(themeError || t('notifications.error') || 'Помилка збереження теми', 'error');
    }
  };



  // Update local colors when theme context colors change
  React.useEffect(() => {
    setLocalColors(colors);
  }, [colors]);

  return (
    <div className="settings-page">
      <div className="settings-card">
        <h1>
          <i className="bi bi-palette"></i>
          {t('profile.settings.customization.title')}
        </h1>
        <p className="page-description">
          {t('profile.settings.theme.description')}
        </p>

        <div className="settings-content">
          {/* Navigation Colors */}
          <div className="color-group">
            <div className="section-header">
              <h3>{t('profile.settings.theme.navigation.title')}</h3>
              <button
                className="reset-section-btn"
                onClick={() => {
                  resetNavigationTheme();
                }}
                disabled={isUpdating}
                title="Reset navigation colors to default"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
            <div className="color-inputs">
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.background')}</span>
                  <input
                    type="color"
                    value={localColors.navigationBg}
                    onChange={(e) => handleColorChange('navigationBg', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationBg}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.text')}</span>
                  <input
                    type="color"
                    value={localColors.navigationText}
                    onChange={(e) => handleColorChange('navigationText', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationText}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.accent')}</span>
                  <input
                    type="color"
                    value={localColors.navigationAccent}
                    onChange={(e) => handleColorChange('navigationAccent', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationAccent}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.hover') || 'Hover Color'}</span>
                  <input
                    type="color"
                    value={localColors.navigationHover}
                    onChange={(e) => handleColorChange('navigationHover', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationHover}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.iconColor') || 'Icon Color'}</span>
                  <input
                    type="color"
                    value={localColors.navigationIconColor}
                    onChange={(e) => handleColorChange('navigationIconColor', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationIconColor}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.navigation.menuText') || 'Menu Text Color'}</span>
                  <input
                    type="color"
                    value={localColors.navigationMenuText}
                    onChange={(e) => handleColorChange('navigationMenuText', e.target.value)}
                  />
                  <span className="color-value">{localColors.navigationMenuText}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Header Colors */}
          <div className="color-group">
            <div className="section-header">
              <h3>{t('profile.settings.theme.header.title')}</h3>
              <button
                className="reset-section-btn"
                onClick={() => {
                  resetHeaderTheme();
                }}
                disabled={isUpdating}
                title="Reset header colors to default"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
            <div className="color-inputs">
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.header.background')}</span>
                  <input
                    type="color"
                    value={localColors.headerBg}
                    onChange={(e) => handleColorChange('headerBg', e.target.value)}
                  />
                  <span className="color-value">{localColors.headerBg}</span>
                </label>
              </div>
              <div className="color-input-wrapper">
                <label>
                  <span>{t('profile.settings.theme.header.text')}</span>
                  <input
                    type="color"
                    value={localColors.headerText}
                    onChange={(e) => handleColorChange('headerText', e.target.value)}
                  />
                  <span className="color-value">{localColors.headerText}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handleSaveTheme}
              disabled={isUpdating}
            >
              <i className="bi bi-check-circle"></i>
              {isUpdating ? t('profile.settings.theme.actions.saving') : t('profile.settings.theme.actions.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizationSettings;
